import { del, get, set } from 'idb-keyval';
import {
  storageName as HOME_STORE_NAME,
  isStoreEmpty as isHomeStoreEmpty,
} from '../store/homeStore';
import {
  storageName as LEGEND_STORE_NAME,
  isStoreEmpty as isLegendStoreEmpty,
} from '../store/legendStore';
import {
  storageName as MAP_STORE_NAME,
  isStoreEmpty as isMapStoreEmpty,
} from '../store/mapStore';
import {
  storageName as MARKER_STORE_NAME,
  isStoreEmpty as isMarkerStoreEmpty,
} from '../store/markerStore';

// eslint-disable-next-line @typescript-eslint/ban-types
const STORE_KEY_EMPTY_COMPARATOR = new Map<string, Function>([
  [MARKER_STORE_NAME, isMarkerStoreEmpty],
  [LEGEND_STORE_NAME, isLegendStoreEmpty],
  [MAP_STORE_NAME, isMapStoreEmpty],
  [HOME_STORE_NAME, isHomeStoreEmpty],
]);

const LAST_ACCESSED_KEY = 'MOOD_store_last_accessed';
const RETENTION_PERIOD_DAYS = 30;
const RETENTION_PERIOD_MS = RETENTION_PERIOD_DAYS * 24 * 60 * 60 * 1000;

/**
 * Updates the last accessed timestamp for the marker store
 */
export const updateLastAccessed = async (): Promise<void> => {
  const currentTime = new Date().getTime();
  await set(LAST_ACCESSED_KEY, currentTime);
};

/**
 * Checks if the marker store cache has expired based on the retention period
 * @returns {Promise<boolean>} True if the cache has expired, false otherwise
 */
export const hasCacheExpired = async (): Promise<boolean> => {
  const lastAccessed = await get(LAST_ACCESSED_KEY);

  if (!lastAccessed) return false;

  const currentTime = new Date().getTime();
  const expirationTime = lastAccessed + RETENTION_PERIOD_MS;

  return currentTime > expirationTime;
};

/**
 * Checks if there is any data in the marker store cache
 * @returns {Promise<boolean>} True if there is cached data, false otherwise
 */
export const hasCache = async (): Promise<boolean> => {
  let hasData = false;
  for (const [key, isEmpty] of STORE_KEY_EMPTY_COMPARATOR) {
    const data: string | undefined = await get(key);

    if (!data) continue;

    const dataObject: { state: unknown } = JSON.parse(data);

    if (isEmpty(dataObject.state)) continue;

    hasData = true;
    break;
  }

  return hasData;
};

/**
 * Clears the marker store cache from IndexedDB
 */
export const clearCache = async (): Promise<void> => {
  for (const [key] of STORE_KEY_EMPTY_COMPARATOR) {
    await del(key);
  }
  await del(LAST_ACCESSED_KEY);
};
